import styles from './DropdownButton.module.scss';
import { cloneElement, isValidElement, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { useOutsideClick } from 'client/utils/hooks/useOutsideClick';
import PropTypes from 'prop-types';
import Icon from '@mdi/react';
import { mdiChevronDown, mdiChevronUp } from '@mdi/js';
import Button from '../Button/Button';
import useDropdownSelection from './useDropdownSelection';
import useDropdownButtons from './useDropdownButtons';
import { useIntl } from 'react-intl';
import defaultAriaProps from 'client/utils/constants/ariaPropTypes';

const DropdownButton = ({ children, variant, isDisabled, selectedIndex, ...props }) => {
    const [open, setOpen] = useState(false);
    const [hasTriggerContent, setHasTriggerContent] = useState(true);
    const intl = useIntl();
    const [chevronSpace, setChevronSpace] = useState(null);
    const closeDropdown = useCallback(() => setOpen(false), []);
    const ref = useOutsideClick(closeDropdown);
    const triggerMainRef = useRef(null);
    const menuRef = useRef(null);

    const baseTestId = props?.['data-testid'];
    const testId = (suffix) => (baseTestId ? `${baseTestId}__${suffix}` : undefined);

    const { buttons, sizerButtons, triggerButtons } = useDropdownButtons({
        children,
        triggerMainButtonClassName: styles.triggerMainButton,
        onTriggerPressed: closeDropdown,
        baseTestId
    });

    const [selected, setSelected] = useDropdownSelection({
        count: buttons.length,
        selectedIndex
    });

    useLayoutEffect(() => {
        const triggerMainEl = triggerMainRef.current;
        const hasRenderedContent = Boolean(
            triggerMainEl && (
                triggerMainEl.childElementCount > 0 ||
                triggerMainEl.textContent?.trim()?.length
            )
        );

        setHasTriggerContent((current) => (current === hasRenderedContent ? current : hasRenderedContent));

        if (!hasRenderedContent) {
            return;
        }

        const triggerButtonEl = triggerMainEl?.querySelector?.('.govuk-button');
        if (!triggerButtonEl) {
            return;
        }

        const height = Math.round(triggerButtonEl.getBoundingClientRect().height);
        if (Number.isFinite(height) && height > 0) {
            setChevronSpace((current) => (current === height ? current : height));
        }
    }, [selected, triggerButtons.length]);

    const isOptionDisabled = useCallback((button, index) => {
        if (!isValidElement(button)) {
            return true;
        }

        const isSelected = index === selected;
        const isChildDisabled = Boolean(button.props?.disabled || button.props?.isDisabled);
        return isSelected || isChildDisabled;
    }, [selected]);

    const getEnabledOptionIndexes = useCallback(() => {
        return buttons.reduce((indexes, button, index) => {
            if (!isOptionDisabled(button, index)) {
                indexes.push(index);
            }
            return indexes;
        }, []);
    }, [buttons, isOptionDisabled]);

    useLayoutEffect(() => {
        if (!open) {
            return;
        }

        const menuElement = menuRef.current;
        if (!menuElement) {
            return;
        }

        const [firstEnabledOptionIndex] = getEnabledOptionIndexes();

        if (!Number.isInteger(firstEnabledOptionIndex)) {
            return;
        }

        // Delay to the next frame so focus is applied after the press interaction settles.
        const animationFrameId = requestAnimationFrame(() => {
            const firstEnabledOption = menuElement.querySelector(
                `[data-dropdown-option-index="${firstEnabledOptionIndex}"]`
            );
            firstEnabledOption?.focus();
        });

        return () => cancelAnimationFrame(animationFrameId);
    }, [getEnabledOptionIndexes, open]);

    const handleMenuKeyDown = useCallback((e) => {
        const { key } = e;
        if (key !== 'ArrowDown' && key !== 'ArrowUp') {
            return;
        }

        const menuElement = menuRef.current;
        if (!menuElement) {
            return;
        }

        const enabledOptionIndexes = getEnabledOptionIndexes();

        if (enabledOptionIndexes.length === 0) {
            return;
        }

        const activeElement = document.activeElement;
        const activeOptionElement = activeElement?.closest?.('[data-dropdown-option-index]');
        const activeOptionIndex = Number(activeOptionElement?.getAttribute('data-dropdown-option-index'));
        const currentEnabledIndex = enabledOptionIndexes.findIndex((index) => index === activeOptionIndex);

        const offset = e.key === 'ArrowDown' ? 1 : -1;
        const nextEnabledIndex = currentEnabledIndex === -1
            ? (offset > 0 ? 0 : enabledOptionIndexes.length - 1)
            : (currentEnabledIndex + offset + enabledOptionIndexes.length) % enabledOptionIndexes.length;

        const nextOptionIndex = enabledOptionIndexes[nextEnabledIndex];
        const nextOption = menuElement.querySelector(`[data-dropdown-option-index="${nextOptionIndex}"]`);
        nextOption?.focus();
        e.preventDefault();
    }, [getEnabledOptionIndexes]);

    const [firstEnabledOptionIndex] = getEnabledOptionIndexes();

    if (buttons.length === 0 || !hasTriggerContent) {
        return null;
    }
    return (
        <div
            ref={ref}
            className={styles.container}
            style={chevronSpace ? { '--chevron-space': `${chevronSpace}px` } : undefined}
            {...props}
        >
            {/* sizer */}
            <div className={styles.widthSizer} aria-hidden="true">
                {sizerButtons.map((button) => (
                    <div key={button.key} className={styles.sizerRow}>
                        {button}
                        <span className={styles.chevronSpacer} />
                    </div>
                ))}
            </div>

            {/* trigger */}
            <div className={styles.triggerWrap}>
                <div className={styles.trigger}>
                    <div
                        ref={triggerMainRef}
                        className={styles.triggerMain}
                    >
                        {triggerButtons[selected]}
                    </div>
                    <Button
                        variant={variant}
                        className={styles.chevronButton}
                        isDisabled={isDisabled}
                        aria-label={intl.formatMessage({ id: 'mapToolbar.dropdown.toggle' })}
                        aria-haspopup="listbox"
                        aria-expanded={open}
                        onPress={() => setOpen((value) => !value)}
                        data-testid={testId('toggle')}
                    >
                        <Icon
                            path={open ? mdiChevronUp : mdiChevronDown}
                            className={styles.chevronIcon}
                        />
                    </Button>
                </div>
            </div>

            {/* dropdown */}
            {open && (
                <div
                    ref={menuRef}
                    className={styles.menu}
                    aria-label={intl.formatMessage({ id: 'mapToolbar.dropdown.options' })}
                    role="listbox"
                    data-testid={testId('menu')}
                    onKeyDown={handleMenuKeyDown}
                >
                    {buttons.map((button, index) => {
                        const isSelected = index === selected;
                        const disabled = isOptionDisabled(button, index);
                        const optionTabIndex = index === firstEnabledOptionIndex ? 0 : -1;

                        const optionButton = isValidElement(button)
                            ? cloneElement(button, {
                                variant: 'transparent',
                                role: 'option',
                                'aria-selected': isSelected,
                                'data-dropdown-option': 'true',
                                'data-dropdown-option-index': index,
                                disabled,
                                isDisabled: disabled,
                                tabIndex: optionTabIndex,
                                'data-testid': testId(`option-${index}`),
                                onPress: (event) => {
                                    button.props?.onPress?.(event);
                                    if (disabled) {
                                        return;
                                    }
                                    setSelected(index);
                                    setOpen(false);
                                }
                            })
                            : button;

                        return (
                            <div
                                key={button.key}
                                className={styles.menuRow}
                                role="presentation"
                                onClick={() => {
                                    if (!isValidElement(button) || disabled) {
                                        return;
                                    }
                                    setSelected(index);
                                    setOpen(false);
                                }}
                            >
                                {optionButton}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

DropdownButton.propTypes = {
    ...defaultAriaProps,
    children: PropTypes.node.isRequired,
    variant: PropTypes.string,
    isDisabled: PropTypes.bool,
    selectedIndex: PropTypes.number
};

DropdownButton.defaultProps = {
    isDisabled: false,
    variant: 'default'
};

export default DropdownButton;
